import React, { useEffect, useRef, useState } from 'react';
import { ArrowRight, Bot, Brain, Check, LayoutGrid, PlugZap, ShieldCheck, Sparkles, X, Zap } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { SlackPanel } from '../components/SlackIntegrationGraph';
import { CodeTerminal, SurfaceTab, TabIcon, type SurfaceKey } from './CompanyBrain';
import { MoreThanAnswers } from './GigaForChatGPT2';
import { ConnectedAppsPillar } from '../components/alt/ConnectedAppsPillar';
import { PlatformPillar } from '../components/alt/PlatformPillar';
import { CompanyBrainPillar } from '../components/alt/CompanyBrainPillar';

// Order carries the argument: connect the tools, build with them, take that
// intelligence to where people work, and let it compound.
const SECTIONS = [{
  id: 'connected-apps',
  label: 'Connected Apps',
  icon: PlugZap
}, {
  id: 'platform',
  label: 'Platform',
  icon: LayoutGrid
}, {
  id: 'co-worker',
  label: 'AI Co Worker',
  icon: Bot
}, {
  id: 'company-brain',
  label: 'Company Brain',
  icon: Brain
}] as const;
type SectionId = (typeof SECTIONS)[number]['id'];
const HEADLINE_ROTATION_MS = 4200;
const HEADLINE_ROTATION_OFFSET_MS = 2100;
export function Alt() {
  const [activeSection, setActiveSection] = useState<SectionId>('connected-apps');
  const [activeSurface, setActiveSurface] = useState<SurfaceKey>('slack');
  const [audienceIndex, setAudienceIndex] = useState(0);
  const navRef = useRef<HTMLDivElement | null>(null);
  const lastScrollYRef = useRef(0);
  const audiencePhrases = ['companies', 'founders', 'operators', 'teams'];
  useEffect(() => {
    let interval: number | undefined;
    const timeout = window.setTimeout(() => {
      setAudienceIndex(current => (current + 1) % audiencePhrases.length);
      interval = window.setInterval(() => {
        setAudienceIndex(current => (current + 1) % audiencePhrases.length);
      }, HEADLINE_ROTATION_MS);
    }, HEADLINE_ROTATION_OFFSET_MS);
    return () => {
      window.clearTimeout(timeout);
      if (interval) window.clearInterval(interval);
    };
  }, [audiencePhrases.length]);
  useEffect(() => {
    const sections = SECTIONS.map(section => document.getElementById(section.id)).filter((section): section is HTMLElement => Boolean(section));
    if (sections.length === 0) return;
    const observer = new IntersectionObserver(entries => {
      const scrollingDown = window.scrollY >= lastScrollYRef.current;
      lastScrollYRef.current = window.scrollY;
      const entering = entries.filter(entry => entry.isIntersecting).sort((a, b) => SECTIONS.findIndex(section => section.id === a.target.id) - SECTIONS.findIndex(section => section.id === b.target.id));
      const next = scrollingDown ? entering[entering.length - 1] : entering[0];
      if (next?.target.id) {
        setActiveSection(next.target.id as SectionId);
      }
    }, {
      rootMargin: '-32% 0px -67% 0px',
      threshold: 0
    });
    sections.forEach(section => observer.observe(section));
    return () => observer.disconnect();
  }, []);
  const scrollToSection = (sectionId: SectionId) => {
    const el = document.getElementById(sectionId);
    if (!el) return;
    const navHeight = navRef.current?.offsetHeight ?? 0;
    const headerOffset = 76;
    const top = el.getBoundingClientRect().top + window.scrollY - navHeight - headerOffset - 22;
    window.scrollTo({
      top,
      behavior: 'smooth'
    });
  };
  return <div className="min-h-screen pt-24 text-[#2B211A]" style={{
    backgroundColor: '#FAF8F4',
    backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='8' height='8' viewBox='0 0 8 8' xmlns='http://www.w3.org/2000/svg'%3E%3Crect width='2' height='2' fill='%232B211A' fill-opacity='0.10'/%3E%3C/svg%3E\")",
    backgroundSize: '8px 8px'
  }} data-id="element-5900" data-mp="1wsv506p7c60k">
      <section className="pb-10 pt-12 md:pb-14 md:pt-20" data-id="element-5901" data-mp="hd4gr7a4kfl0">
        <div className="mx-auto max-w-[1280px] px-6 md:px-10" data-id="element-5902" data-mp="cczz3dxhdet7">
          <div className="mx-auto max-w-5xl text-center" data-id="element-5903" data-mp="ceugii9jmosj">
            <h1 className="font-title text-5xl font-bold leading-[0.95] tracking-tight text-[#2B211A] md:text-7xl" data-id="element-5904" data-mp="2zm8nxvybndcj">
              The AI Platform for{' '}
              <span className="inline-flex overflow-hidden align-baseline" data-id="element-5905" data-mp="34md8bx2cos81">
                <AnimatePresence mode="wait" initial={false} data-id="element-5906" data-mp="39k526wmk2oev">
                  <motion.span key={audiencePhrases[audienceIndex]} initial={{
                  y: '100%',
                  opacity: 0,
                  rotateX: -60
                }} animate={{
                  y: 0,
                  opacity: 1,
                  rotateX: 0
                }} exit={{
                  y: '-100%',
                  opacity: 0,
                  rotateX: 60
                }} transition={{
                  duration: 0.42,
                  ease: [0.22, 1, 0.36, 1]
                }} className="block" data-id="element-5907" data-mp="3dli5ayy9kdi3">
                    {audiencePhrases[audienceIndex]}
                  </motion.span>
                </AnimatePresence>
              </span>
              <br data-id="element-5908" data-mp="1umakiv73xgon" />
              that want to <HeadlineIcon icon={Zap} data-id="element-5909" data-mp="1ul96chusoe2p" />
              <UnderscoredPhrase data-id="element-5910" data-mp="1uk6ea6lqk5l7">Go Faster</UnderscoredPhrase>
              <br data-id="element-5911" data-mp="1por0tfrn4bpb" />
              and <HeadlineIcon icon={ShieldCheck} data-id="element-5912" data-mp="1pld00bymkajz" />
              <UnderscoredPhrase data-id="element-5913" data-mp="1ll2wr727vy8t">Stay in Control</UnderscoredPhrase>
            </h1>

            {/* The whole page in one line, so the four tabs below read as a
             sequence rather than a menu of features. */}
            <p className="mx-auto mt-8 max-w-[64ch] text-lg leading-relaxed text-[#6B5D52] md:text-xl" data-id="element-5914" data-mp="1bogacdu8sz4o">
              Connect the tools your company runs on, build the way your team
              works with AI, put that AI everywhere people already work, and let
              what it learns compound.
            </p>
          </div>
        </div>
      </section>

      <div ref={navRef} className="sticky top-[72px] z-40" data-id="element-5915" data-mp="swx9odnoxx5t">
        <nav className="mx-auto grid max-w-[1280px] grid-cols-2 gap-2 px-3 py-4 sm:grid-cols-4 sm:gap-3 sm:px-6 md:flex md:justify-center md:px-10" aria-label="Section navigation" data-id="element-5916" data-mp="swf8d2m68sr1">
          {SECTIONS.map(section => {
          const isActive = activeSection === section.id;
          const Icon = section.icon;
          return <button key={section.id} type="button" onClick={() => scrollToSection(section.id)} className={`inline-flex min-w-0 items-center justify-center gap-1.5 rounded-full border px-2 py-2.5 text-[clamp(0.72rem,2.7vw,1rem)] font-semibold shadow-sm transition-all sm:gap-2 sm:px-4 md:shrink-0 md:px-5 md:text-lg ${isActive ? 'border-[#EA580C] bg-[#EA580C] text-white shadow-md hover:bg-[#C2410C]' : 'border-neutral-200 bg-white text-neutral-600 hover:border-[#EA580C]/30 hover:text-[#2B211A]'}`} aria-current={isActive ? 'true' : undefined} data-id="element-5917" data-mp="4cbquwqcliq8">
                <Icon size={19} className={`shrink-0 ${isActive ? 'text-white' : 'text-neutral-400'}`} data-id="element-5918" data-mp="s97reyemgwlk" />
                <span className="min-w-0 leading-tight" data-id="element-5919" data-mp="1fz21j87k4724">{section.label}</span>
              </button>;
        })}
        </nav>
      </div>

      <main data-id="element-5920" data-mp="1kseo7csoe3s8">
        <section id="connected-apps" className="scroll-mt-40 border-b border-neutral-200/70 bg-[#FAF8F4] py-20 md:py-28" data-id="element-5921" data-mp="35gz6hymy4lo8">
          <div className="mx-auto max-w-[1280px] px-6 md:px-10" data-id="element-5922" data-mp="30ie4bto4vrx5">
            <ConnectedAppsPillar data-id="element-5923" data-mp="30g1phpdo9xob" />
            <div className="mt-20 md:mt-28" data-id="element-5924" data-mp="3f71xxuef5rkg">
              <h3 className="mb-8 font-title text-3xl font-bold leading-tight tracking-tight text-[#2B211A] md:text-4xl" data-id="element-5925" data-mp="3f5zci1zv8hbx">
                Why not just use OpenAI or Anthropic?
              </h3>
              <ConnectedAppsTable data-id="element-5926" data-mp="2mid5ejrfiukb" />
            </div>
          </div>
        </section>

        <section id="platform" className="scroll-mt-40 border-b border-neutral-200/70 bg-white py-20 md:py-28" data-id="element-5927" data-mp="2hkl2kup8xo1c">
          <div className="mx-auto max-w-[1280px] px-6 md:px-10" data-id="element-5928" data-mp="2vk4bvqtsn5f8">
            <PlatformPillar data-id="element-5929" data-mp="2rg4wxco5wenz" />
          </div>
        </section>

        <section id="co-worker" className="scroll-mt-40 border-b border-neutral-200/70 bg-[#FAF8F4] py-20 md:py-28" data-id="element-5930" data-mp="ask9rsucjffa">
          <div className="mx-auto max-w-[1280px] px-6 md:px-10" data-id="element-5931" data-mp="6ria5awip1cs">
            <div className="mx-auto mb-12 max-w-3xl text-center md:mb-16" data-id="element-5932" data-mp="6tbgsq74rlks">
              <p className="mb-4 text-[12px] font-bold uppercase tracking-[0.14em] text-[#EA580C]" data-id="element-5933" data-mp="20biuv25lz6pz">
                Everywhere your team works
              </p>
              <h2 className="font-title text-4xl font-bold leading-[1.05] tracking-tight text-[#2B211A] md:text-[56px]" data-id="element-5934" data-mp="2a7luo1czmt9r">
                Your AI doesn't have to
                <br data-id="element-5935" data-mp="2f0f24wzucfpj" />
                live in another app.
              </h2>
              <p className="mx-auto mt-5 max-w-[54ch] text-lg leading-relaxed text-[#6B5D52]" data-id="element-5936" data-mp="2izxl3n83qgpq">
                Everything your company builds in Giga can show up as a co
                worker in Slack, ChatGPT, and Claude Code — with Teams and
                iMessage next. Same connections, same brain, no new tab.
              </p>
            </div>

            <div className="grid items-center gap-10 md:grid-cols-[1.2fr_0.8fr]" data-id="element-5937" data-mp="ykvp24amyv4q">
              <SurfacePreview activeSurface={activeSurface} setActiveSurface={setActiveSurface} data-id="element-5938" data-mp="13j5h4612dfm4" />

              <div data-id="element-5939" data-mp="3gtgf3eh0b8ja">
                <SurfaceTabsControl activeSurface={activeSurface} setActiveSurface={setActiveSurface} className="mb-8 hidden md:flex" tabClassName="!px-4 !py-2.5 !text-base" data-id="element-5940" data-mp="3gtyhyty6cl06" />

                <div className="space-y-4 font-title text-xl text-[#2B211A] md:text-2xl" style={{
                fontWeight: 500
              }} data-id="element-5941" data-mp="38150qqz63bjo">
                  <IconBullet icon={PlugZap} label="Reads and writes in your connected tools" data-id="element-5942" data-mp="333v2qmxpprk8" />
                  <IconBullet icon={Zap} label="Real work: audits, analysis, reports, presentations" data-id="element-5943" data-mp="3lve96u44nkj" />
                  <IconBullet icon={Sparkles} label="Autonomous and proactive" data-id="element-5944" data-mp="3vrh73xerl085" />
                </div>
                <p className="mt-6 max-w-[46ch] text-[15px] leading-relaxed text-[#6B5D52]" data-id="element-5945" data-mp="3qqbcfu1jn2vz">
                  The co worker is one interface into the platform — not a
                  separate product with its own setup to maintain.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section id="company-brain" className="scroll-mt-40 bg-white py-20 md:py-28" data-id="element-5946" data-mp="2afz95r3fwfro">
          <div className="mx-auto max-w-[1280px] px-6 md:px-10" data-id="element-5947" data-mp="20uykiev9mpuv">
            <CompanyBrainPillar data-id="element-5948" data-mp="1vwn6drxxtt6u" />
          </div>
        </section>

        <section className="bg-[#FAF8F4] py-20 md:py-28" data-id="element-5949" data-mp="2ak8anp6jtygc">
          <div className="mx-auto max-w-[1280px] px-6 md:px-10" data-id="element-5950" data-mp="2alt5lfwrzjeq">
            <AccelerationControlSection data-id="element-5951" data-mp="25sqklw62dia5" />
            <ClosingCTA data-id="element-5952" data-mp="25roxgtbrx5c2" />
          </div>
        </section>
      </main>
    </div>;
}
// Sized in em so the tile tracks the headline as it scales across breakpoints.
function HeadlineIcon({
  icon: Icon
}: {
  icon: React.ElementType;
}) {
  return <span aria-hidden="true" className="mr-[0.18em] inline-flex size-[0.86em] -translate-y-[0.06em] items-center justify-center rounded-[0.2em] bg-[#EA580C] align-middle" data-id="element-5953" data-mp="2tjvyi5sg20ht">
      <Icon className="size-[0.56em] text-white" strokeWidth={2.6} data-id="element-5954" data-mp="z5bii6f2it4r" />
    </span>;
}
function UnderscoredPhrase({
  children
}: {
  children: React.ReactNode;
}) {
  return <span className="relative inline-block align-baseline" data-id="element-5955" data-mp="u7t80blek5un">
      <span className="absolute inset-x-0 bottom-[-4px] h-[9px] bg-[#EA580C]" aria-hidden="true" data-id="element-5956" data-mp="35t26ibaf3cjf" />
      <span className="relative z-10" data-id="element-5957" data-mp="3at6o64n2adb8">{children}</span>
    </span>;
}
function SurfacePreview({
  activeSurface,
  setActiveSurface
}: {
  activeSurface: SurfaceKey;
  setActiveSurface: (surface: SurfaceKey) => void;
}) {
  const mobileTabs = <SurfaceTabsControl activeSurface={activeSurface} setActiveSurface={setActiveSurface} className="relative z-10 flex md:hidden" tabClassName="!flex-1 !justify-center !px-2 !py-2 !text-xs" quiet data-id="element-5958" data-mp="311amykpoi72s" />;
  if (activeSurface === 'chatgpt') {
    return <div className="relative mx-auto w-full max-w-[760px] rounded-[28px] bg-[#EA580C]/10 p-2 pt-1 md:bg-transparent md:p-0" data-id="element-5959" data-mp="2d56892579ckj">
        <div className="absolute -inset-3 hidden rounded-[28px] bg-[#EA580C]/10 md:block" aria-hidden="true" data-id="element-5960" data-mp="2i52df4pmva1o" />
        {mobileTabs}
        <div className="relative rounded-[22px] rounded-tr-none bg-white p-3 text-left shadow-[0_28px_80px_rgba(43,33,26,0.13)] md:rounded-[24px]" data-id="element-5961" data-mp="becn7zcg0355">
          <MoreThanAnswers embedded data-id="element-5962" data-mp="ew0ffv1dt1e7" />
        </div>
      </div>;
  }
  if (activeSurface === 'code') {
    return <div className="relative mx-auto w-full max-w-[760px] rounded-[28px] bg-[#EA580C]/10 p-2 pt-1 md:bg-transparent md:p-0" data-id="element-5963" data-mp="9w4l6nihkobu">
        {mobileTabs}
        <CodeTerminal data-id="element-5964" data-mp="4zdtt418e6b4" />
      </div>;
  }
  return <div className="relative mx-auto w-full max-w-[760px] rounded-[28px] bg-[#EA580C]/10 p-2 pt-1 md:bg-transparent md:p-0" data-id="element-5965" data-mp="3w444znuvgcax">
      <div className="absolute -inset-3 hidden rounded-[28px] bg-[#EA580C]/10 md:block" aria-hidden="true" data-id="element-5966" data-mp="xkni66w35eom" />
      {mobileTabs}
      <div className="relative text-left [&>div]:rounded-tl-none md:[&>div]:rounded-tl-2xl" data-id="element-5967" data-mp="soyr6ry4t63x">
        <SlackPanel data-id="element-5968" data-mp="nucfsos1vdju" />
      </div>
    </div>;
}
function SurfaceTabsControl({
  activeSurface,
  setActiveSurface,
  className = '',
  tabClassName = '',
  quiet = false
}: {
  activeSurface: SurfaceKey;
  setActiveSurface: (surface: SurfaceKey) => void;
  className?: string;
  tabClassName?: string;
  quiet?: boolean;
}) {
  if (quiet) {
    const tabs = [{
      surface: 'slack' as const,
      label: 'Slack',
      icon: <TabIcon src="https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/slack.svg" fallback="S" data-id="element-5969" data-mp="1jwqg0b1ud3uh" />
    }, {
      surface: 'code' as const,
      label: 'Claude',
      icon: <TabIcon slug="claude" fallback="C" data-id="element-5970" data-mp="1str1o9xj0nu8" />,
      ariaLabel: 'Claude'
    }, {
      surface: 'chatgpt' as const,
      label: 'ChatGPT',
      icon: <TabIcon src="https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/openai.svg" fallback="AI" data-id="element-5971" data-mp="22ti18q0y1t16" />
    }];
    return <div className={`w-full items-center gap-0 ${className}`} role="tablist" aria-label="Chat surface" data-id="element-5972" data-mp="26u1ydtliownb">
        {tabs.map(tab => {
        const active = activeSurface === tab.surface;
        return <button key={tab.surface} type="button" role="tab" aria-selected={active} aria-label={tab.ariaLabel} onClick={() => setActiveSurface(tab.surface)} className={`inline-flex items-center gap-1.5 rounded-t-[16px] border border-transparent px-2 py-2 text-xs text-[#2B211A] transition-colors ${active ? 'bg-white font-bold shadow-sm' : 'font-semibold text-[#6B5D52]'} ${tabClassName}`} data-id="element-5973" data-mp="2gkwjuvqxvs7p">
              {tab.icon}
              {tab.label}
            </button>;
      })}
      </div>;
  }
  return <div className={`w-full items-center gap-2.5 md:gap-3 ${className}`} role="tablist" aria-label="Chat surface" data-id="element-5974" data-mp="v4qhytho09y">
      <SurfaceTab surface="slack" activeSurface={activeSurface} setActiveSurface={setActiveSurface} className={tabClassName} data-id="element-5975" data-mp="3s1z6r3wrlbbm">
        <TabIcon src="https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/slack.svg" fallback="S" data-id="element-5976" data-mp="34blno2w5qmt6" />
        Slack
      </SurfaceTab>
      <SurfaceTab surface="code" activeSurface={activeSurface} setActiveSurface={setActiveSurface} ariaLabel="Claude" className={tabClassName} data-id="element-5977" data-mp="3dbrwvcktp0mj">
        <TabIcon slug="claude" fallback="C" data-id="element-5978" data-mp="399n74oxhwo56" />
        Claude
      </SurfaceTab>
      <SurfaceTab surface="chatgpt" activeSurface={activeSurface} setActiveSurface={setActiveSurface} className={tabClassName} data-id="element-5979" data-mp="sl16ktz6yqbl">
        <TabIcon src="https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/openai.svg" fallback="AI" data-id="element-5980" data-mp="ojny0c4ps90z" />
        ChatGPT
      </SurfaceTab>
    </div>;
}
const ACCELERATION_CONTROL_GROUPS = [{
  heading: 'Go Faster',
  items: [{
    title: 'Offload busywork and associate work',
    copy: 'Hand Giga the repetitive research, formatting, follow-up, and first-draft work so your team can spend more time on judgment calls.'
  }, {
    title: 'Kill Meetings',
    copy: 'Turn scattered conversations into crisp updates, decisions, and next steps without adding another recurring sync to the calendar.'
  }, {
    title: 'Stop waiting for numbers',
    copy: 'Pull live metrics from the systems you already use and get answers, reports, and analysis without waiting on manual exports.'
  }]
}, {
  heading: 'Stay In Control',
  items: [{
    title: 'Own your own data',
    copy: 'Export your data, keep your history portable, and switch systems whenever you need without losing the work your team has built.'
  }, {
    title: 'Get Real Time Visibility',
    copy: 'See what is connected, what is being used, and where sensitive work is happening across your team as it happens.'
  }, {
    title: 'Built for teams',
    copy: 'Manage shared access, team workflows, and company context from one place instead of stitching together personal AI accounts.'
  }]
}] as const;
function AccelerationControlSection() {
  return <div data-id="element-5981" data-mp="1yi44ek3ltqte">
      {ACCELERATION_CONTROL_GROUPS.map((group, groupIndex) => <section key={group.heading} className={groupIndex > 0 ? 'mt-16 md:mt-20' : undefined} data-id="element-5982" data-mp="1thos7vv26pd0" data-mpi={groupIndex}>
          <h2 className="font-title text-4xl font-bold leading-tight text-[#2B211A] md:text-6xl" data-id="element-5983" data-mp="1op5bwr4zlg4i">
            {group.heading}
          </h2>
          <div className="mt-8 border-y border-neutral-200" data-id="element-5984" data-mp="1jor51cf4gc82">
            {group.items.map((item, index) => <motion.article key={item.title} initial={{
          opacity: 0,
          y: 18
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-80px'
        }} transition={{
          duration: 0.42,
          delay: index * 0.03,
          ease: [0.22, 1, 0.36, 1]
        }} className="grid gap-4 border-b border-neutral-200 py-8 last:border-b-0 md:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] md:items-center md:gap-8 md:py-10" data-id="element-5985" data-mp="1fnfen4ofbmyt" data-mpi={index}>
                <h3 className="font-title text-2xl font-bold leading-tight text-[#2B211A] md:text-3xl" data-id="element-5986" data-mp="3vegu2kiqnggi">{item.title}</h3>
                <p className="max-w-xl text-base leading-7 text-[#6B5D52] md:text-lg md:leading-8" data-id="element-5987" data-mp="2yopzymeczvb8">{item.copy}</p>
              </motion.article>)}
          </div>
        </section>)}
    </div>;
}
function ClosingCTA() {
  return <div className="mt-20 overflow-hidden rounded-[28px] bg-[#2B211A] px-8 py-14 text-center md:mt-28 md:px-16 md:py-20" data-id="element-5988" data-mp="37ntklut6ciru">
      <h2 className="mx-auto max-w-[24ch] font-title text-4xl font-bold leading-[1.05] tracking-tight text-white md:text-[52px]" data-id="element-5989" data-mp="3ch5ueyt72l0j">
        One platform to run your company's AI on.
      </h2>
      <p className="mx-auto mt-5 max-w-[52ch] text-lg leading-relaxed text-white/65" data-id="element-5990" data-mp="2jxz6l9x4co60">
        Connect your tools, build your workflows, put a co worker where your
        team already is, and let the brain get sharper every week.
      </p>
      <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row" data-id="element-5991" data-mp="18f96inqo5fjo">
        <Link to="/signup" className="inline-flex items-center gap-2 rounded-full bg-[#EA580C] px-7 py-3.5 text-base font-bold text-white transition-colors hover:bg-[#C2410C]" data-id="element-5992" data-mp="1n82oqgxbs9s1">
          Get started for free
          <ArrowRight size={17} data-id="element-5993" data-mp="1w8hbzltunyy6" />
        </Link>
        <Link to="/demo/book" className="inline-flex items-center rounded-full border border-white/25 px-7 py-3.5 text-base font-bold text-white transition-colors hover:border-white/60" data-id="element-5994" data-mp="1rapkuu114gv1">
          Book a demo
        </Link>
      </div>
      <p className="mt-6 text-[13.5px] text-white/45" data-id="element-5995" data-mp="213n4x4lo6dvr">
        $100 in credits included · no card needed
      </p>
    </div>;
}
type ConnectedAppsValue = boolean | 'Enterprise only' | 'Coming soon' | null;
const CONNECTED_APPS_ROWS: Array<{
  feature: string;
  description: string;
  chatgpt: ConnectedAppsValue;
  anthropic: ConnectedAppsValue;
  giga: ConnectedAppsValue;
}> = [{
  feature: 'Supports MCP connections',
  description: 'Connect through existing MCP servers from supported providers.',
  chatgpt: true,
  anthropic: true,
  giga: true
}, {
  feature: 'Supports API connections',
  description: 'Stop having your team paste API tokens into conversations in plain text.',
  chatgpt: false,
  anthropic: false,
  giga: true
}, {
  feature: 'Supports SQL connections',
  description: 'See which databases your team connects to, review their permissions, and flag anything that looks dangerous.',
  chatgpt: false,
  anthropic: false,
  giga: true
}, {
  feature: 'Supports email/password connections',
  description: "Use Giga's cloud browser for tools without an API or MCP, so your agent can drive the browser and finish the work.",
  chatgpt: false,
  anthropic: false,
  giga: true
}, {
  feature: 'Supports unlisted tools',
  description: 'Use the on-demand integration wizard to add tools that are not yet in our directory.',
  chatgpt: false,
  anthropic: false,
  giga: true
}, {
  feature: 'Supports multiple accounts',
  description: 'Connect personal, team, client, and environment accounts without repeatedly signing in and out.',
  chatgpt: false,
  anthropic: false,
  giga: true
}, {
  feature: "Gives admins full visibility on the team's connections",
  description: 'Give admins one view of every connection, its owner, permissions, and recent use.',
  chatgpt: false,
  anthropic: false,
  giga: true
}, {
  feature: 'See what tools employees have connected',
  description: 'Understand which tools employees have authorized and where company data can flow.',
  chatgpt: 'Enterprise only',
  anthropic: 'Enterprise only',
  giga: true
}, {
  feature: 'Bring your own OAuth providers',
  description: 'Use your own OAuth app and consent screen when you need complete control over authorization.',
  chatgpt: null,
  anthropic: null,
  giga: true
}, {
  feature: 'Audit activity',
  description: 'Review how connections are used, by whom, and when, with detailed activity history.',
  chatgpt: null,
  anthropic: null,
  giga: 'Coming soon'
}];
function ConnectedAppsTable() {
  return <div className="mx-auto max-w-[900px] overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-[0_30px_80px_-48px_rgba(43,33,26,0.4)]" data-id="element-5996" data-mp="zxlqu0udkxww">
      <div className="overflow-x-auto" data-id="element-5997" data-mp="85ms56punh0k">
        <table className="w-full min-w-[660px] border-collapse text-left" data-id="element-5998" data-mp="86g0gc4giekt">
          <thead data-id="element-5999" data-mp="373512zirz6m">
            <tr className="border-b border-neutral-200" data-id="element-6000" data-mp="37l407dkgri8">
              <th scope="col" className="w-[48%] px-5 py-4 md:px-6" data-id="element-6001" data-mp="h6cfdm11nz2o">
                <span className="sr-only" data-id="element-6002" data-mp="c6ggg177extz">Feature</span>
              </th>
              <th scope="col" className="w-[17%] px-4 py-4 font-title text-[15px] font-bold text-[#2B211A] md:text-base" data-id="element-6003" data-mp="c8jdv6aort8q">
                ChatGPT
              </th>
              <th scope="col" className="w-[17%] px-4 py-4 font-title text-[15px] font-bold text-[#2B211A] md:text-base" data-id="element-6004" data-mp="1nkk8h1u8ebwe">
                Claude
              </th>
              <th scope="col" className="w-[18%] bg-[#FFF8F3] px-4 py-4 font-title text-[15px] font-bold text-[#2B211A] md:text-base" data-id="element-6005" data-mp="q6ippjbkkcw1">
                Giga
              </th>
            </tr>
          </thead>
          <tbody data-id="element-6006" data-mp="z7ypyy8o6ymj">
            {CONNECTED_APPS_ROWS.map(row => <tr key={row.feature} className="border-b border-neutral-100 last:border-b-0" data-id="element-6007" data-mp="z5m96oeneub2">
                <th scope="row" className="px-5 py-3.5 text-[14px] font-semibold leading-snug text-[#2B211A] md:px-6" data-id="element-6008" data-mp="2fczqbbx0480">
                  <span className="block" data-id="element-6009" data-mp="bi4f2m7wvk4x">{row.feature}</span>
                  <span className="mt-1 block max-w-xl text-[12.5px] font-normal leading-[1.45] text-[#8A7B70]" data-id="element-6010" data-mp="bgkz6wq63b3o">
                    {row.description}
                  </span>
                </th>
                <td className="px-4 py-3.5 text-[#2B211A]" data-id="element-6011" data-mp="3eybiqsqbthdp">
                  <ComparisonValue value={row.chatgpt} data-id="element-6012" data-mp="3jxfp7blv910c" />
                </td>
                <td className="px-4 py-3.5 text-[#2B211A]" data-id="element-6013" data-mp="3ceetvdl7ydo2">
                  <ComparisonValue value={row.anthropic} data-id="element-6014" data-mp="3cdwpftoekt52" />
                </td>
                <td className="bg-[#FFF8F3] px-4 py-3.5 text-[#2B211A]" data-id="element-6015" data-mp="37gdbkuvmaidn">
                  <ComparisonValue value={row.giga} highlight data-id="element-6016" data-mp="32fz53h9nzghq" />
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>
    </div>;
}
function ComparisonValue({
  value,
  highlight = false
}: {
  value: ConnectedAppsValue;
  highlight?: boolean;
}) {
  if (value === true) {
    return <span className={`inline-flex items-center ${highlight ? 'text-[#EA580C]' : 'text-[#2B211A]'}`} aria-label="Supported" data-id="element-6017" data-mp="2ooc3r30v7hs3">
        <Check size={19} strokeWidth={2.6} aria-hidden="true" data-id="element-6018" data-mp="2jqizwgn1azuz" />
      </span>;
  }
  if (value === false) {
    return <span className="inline-flex items-center text-neutral-300" aria-label="Not supported" data-id="element-6019" data-mp="2es96aagyk7bh">
        <X size={19} strokeWidth={2.4} aria-hidden="true" data-id="element-6020" data-mp="2aojhpl0a9pqg" />
      </span>;
  }
  if (value === null) {
    return <span aria-label="Not available" className="text-neutral-300" data-id="element-6021" data-mp="25qhwoujargcc">—</span>;
  }
  return <span className={`text-[12px] font-semibold leading-tight ${highlight ? 'text-[#EA580C]' : 'text-[#6B5D52]'}`} data-id="element-6022" data-mp="9o4u86va3zjm">{value}</span>;
}
function IconBullet({
  icon: Icon,
  label
}: {
  icon: React.ElementType;
  label: string;
}) {
  return <p className="flex items-center gap-3" data-id="element-6023" data-mp="3w0bpvo6o90bz">
      <span className="flex size-6 shrink-0 items-center justify-center text-[#EA580C]" data-id="element-6024" data-mp="4vbnxek5wj5f">
        <Icon size={22} strokeWidth={2.4} data-id="element-6025" data-mp="3m3qm166e1v9q" />
      </span>
      {label}
    </p>;
}